import { useCallback, useRef, useState } from 'react'

import type { UserSearchResult } from '@disco/api/server'
import api from '@disco/api/server'
import captureError, { parseServerError } from '@disco/utils/captureError'
import { getEmailHandleFromAngularBrackets } from '@disco/utils/email'

/**
 * Configuration options for the useRecipientSearch hook
 */
interface UseRecipientSearchProps {
  /** Whether to include users with blank/empty email addresses in results */
  includeBlankEmails?: boolean
  /** Whether to hide business entities from search results */
  hideBusinesses?: boolean
  /** Whether to include briefs in search results */
  includeBriefs?: boolean
  /** Whether to automatically trigger search suggestions (vs manual search only) */
  autoSuggest?: boolean
  /** Function to filter search results by type */
  resultFilter?: (result: UserSearchResult) => boolean
}

/**
 * useRecipientSearch - React hook for searching DISCO users, businesses, and email addresses
 *
 * This hook provides functionality for searching recipients when sharing briefs or other content.
 * It integrates with the DISCO API to find users, businesses, groups, and email addresses based
 * on user input with debounced search and proper request cancellation.
 *
 * Features:
 * - Debounced API search with automatic request cancellation
 * - Support for different result types (users, businesses, groups, emails, saved recipients)
 * - Email parsing from angular bracket format (e.g., "John Doe <john@example.com>")
 * - Error handling with user-friendly error states
 * - Loading state management
 * - Option creation for new email addresses
 * - Configurable search options (blank emails, business visibility)
 * - Automatic handling of saved recipients with associated businesses
 * - Saved recipients are displayed as users in dropdown options for consistent UI
 * - Results can be filtered using a custom filter function
 *
 * Search behavior:
 * - Searches are triggered when input length > 2 characters
 * - Dropdown is only displayed when there are search results (3+ characters)
 * - Previous requests are automatically cancelled when new search starts
 * - Results are filtered using the provided resultFilter function (defaults to email, user, saved types)
 * - Results are mapped to consistent format for use with react-select or custom dropdowns
 * - Error states are handled gracefully with disabled error options
 *
 * @param props - Configuration options for the search hook
 * @param props.includeBlankEmails - Whether to include users with blank/empty email addresses
 * @param props.hideBusinesses - Whether to exclude business entities from results
 * @param props.autoSuggest - Whether to automatically trigger search (vs manual only)
 * @param props.resultFilter - Function to filter search results by type
 * @returns Object containing search functions, state, and cleanup utilities
 *
 * @example
 * ```tsx
 * const { searchRecipients, suggestions, loading, cleanup } = useRecipientSearch({
 *   includeBlankEmails: false,
 *   hideBusinesses: false,
 *   autoSuggest: true,
 *   resultFilter: (result) => ['email', 'user', 'saved'].includes(result.type),
 * });
 *
 * useEffect(() => {
 *   if (inputValue.length >= 1) {
 *     const timeoutId = setTimeout(() => {
 *       searchRecipients(inputValue, (error, result) => {
 *         // Results are handled by hook's internal state
 *       });
 *     }, 300);
 *     return () => clearTimeout(timeoutId);
 *   }
 * }, [inputValue, searchRecipients]);
 *
 * // Cleanup on unmount
 * useEffect(() => cleanup, [cleanup]);
 * ```
 */
export const useRecipientSearch = ({
  includeBlankEmails = false,
  hideBusinesses = false,
  includeBriefs = false,
  autoSuggest = true,
  resultFilter = (result: UserSearchResult) =>
    ['email', 'user', 'saved', 'brief'].includes(result.type),
}: UseRecipientSearchProps = {}) => {
  const [suggestions, setSuggestions] = useState<UserSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Searches for recipients based on the provided query string
   *
   * This function performs a debounced search against the DISCO API to find matching users,
   * businesses, groups, and email addresses. It automatically cancels previous requests
   * and handles errors gracefully. API errors are automatically cleared when starting
   * new searches to provide a better user experience.
   *
   * @param query - The search query string (email, name, or partial match)
   * @param callback - Callback function to handle search results or errors
   */
  const searchRecipients = useCallback(
    (
      query: string,
      callback: (error: any, result: { options: UserSearchResult[] }) => void,
    ) => {
      const emailHandle = getEmailHandleFromAngularBrackets(query)

      // Only perform search and show dropdown when query has 3+ characters
      if (autoSuggest && emailHandle.email.length > 2) {
        // Abort previous request if it exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        // Reset API error when starting a new search
        setApiError(null)
        setLoading(true)
        setSuggestions([])
        abortControllerRef.current = new AbortController()

        api
          .getSearchResults(
            'share',
            emailHandle.email,
            {
              includeBlankEmails,
              hideBusinesses,
              includeBriefs,
            },
            abortControllerRef.current.signal,
          )
          .then(results => {
            // Filter results using the provided filter function
            const options = results.filter(resultFilter)

            setSuggestions(options)
            setLoading(false)
            setApiError(null)
            callback(null, { options })
          })
          .catch(e => {
            if (e.name === 'AbortError') {
              // Request was aborted, don't update state
              return
            }

            const errorMessage = e?.body
              ? e?.body?.[0] || parseServerError(e?.body)
              : 'An error occurred while searching for recipients'

            setApiError(errorMessage)
            setLoading(false)

            callback(null, {
              options: [],
            })
          })
      } else {
        // Reset API error when input is too short for search
        setApiError(null)
        // Clear suggestions when input is too short to prevent dropdown display
        // This ensures the dropdown only shows when there are actual search results
        setSuggestions([])
        setLoading(false)
        callback(null, { options: [] })
      }
    },
    [
      autoSuggest,
      includeBlankEmails,
      hideBusinesses,
      includeBriefs,
      resultFilter,
    ],
  )

  /**
   * Creates a new option object from a label string, typically an email address
   *
   * This function is used to create new email recipient options when the user
   * enters an email address that doesn't exist in the search results. It parses
   * the input to extract email and name components from angular bracket format.
   *
   * @param option - Configuration object for the new option
   * @param option.label - The input string (e.g., "John Doe <john@example.com>" or "john@example.com")
   * @param option.labelKey - Key to use for the label field (defaults to 'label')
   * @param option.valueKey - Key to use for the value field (defaults to 'value')
   * @returns Formatted option object compatible with SearchOption interface
   */
  const createNewOption = useCallback(
    (option: { label: string; labelKey?: string; valueKey?: string }) => {
      const { label, labelKey = 'label', valueKey = 'value' } = option
      const emailHandle = getEmailHandleFromAngularBrackets(label)

      return {
        // Extend object to make it work in RecipientTag and react-select
        [valueKey]: label,
        [labelKey]: label,
        id: emailHandle.email,
        value: emailHandle.email,
        email: emailHandle.email,
        name: emailHandle.name || emailHandle.email,
        type: 'email',
      }
    },
    [],
  )

  /**
   * Cleanup function to abort any ongoing search requests
   *
   * This function should be called when the component using this hook unmounts
   * or when you need to cancel pending API requests to prevent memory leaks
   * and state updates on unmounted components.
   *
   * @example
   * ```tsx
   * useEffect(() => {
   *   return cleanup; // Cleanup on unmount
   * }, [cleanup]);
   * ```
   */
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  return {
    searchRecipients,
    createNewOption,
    suggestions,
    loading,
    apiError,
    cleanup,
  }
}
